import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { addDays, differenceInCalendarDays, format, startOfDay } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CalendarIcon16pxSvg from "../../../assets/images/icons/CalendarIcon16pxSvg";
import CustomAutocomplete from "../../../components/CustomAutocomplete";
import {
  ControlpanelPrimaryButton,
  ControlpanelPrimaryButtonV2,
} from "../../../components/StyledButton";
import CustomDatePicker from "../../../components/CustomDatePicker";
import AddRosterItemsDialog from "./AddRosterItemsDialog";
import ClassicView from "./ClassicView";
import SimpleView from "./SimpleView";
import AddUserRoster from "./AddUserRoster";
import { useAuth } from "../../../context/AuthContext";
import { LoadingProvider, useLoading } from "../../../context/LoadingContext";
import CustomDateTimePicker from "../../../components/CustomDateTimeRangePicker";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { IconButton, Tooltip } from "@mui/material";
import AddUserGroupIconpxSvg from "../../../assets/images/icons/AddUserGroupIconpxSvg";
import CalendarToday from "../../../assets/images/icons/CalendarToday";

const WEEK_DAY_NAME_TO_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const WEEK_DAY_NAME_TO_TRANSLATION_KEY = {
  Sunday: "common.sunday",
  Monday: "common.monday",
  Tuesday: "common.tuesday",
  Wednesday: "common.wednesday",
  Thursday: "common.thursday",
  Friday: "common.friday",
  Saturday: "common.saturday",
};

/**
 * Căn về đầu tuần CHỨA ngày cho trước (lùi về weekStartDay).
 * Ví dụ: start Thứ 5 10/09, weekStart=Monday → 07/09 (không nhảy sang 14/09).
 */
const alignToContainingWeekStartDay = (date, weekStartDay) => {
  const weekStartsOn = WEEK_DAY_NAME_TO_INDEX[weekStartDay];
  const normalized = startOfDay(date);

  if (weekStartsOn === undefined) return normalized;

  const daysSinceWeekStart = (normalized.getDay() - weekStartsOn + 7) % 7;
  return addDays(normalized, -daysSinceWeekStart);
};

export const getRosterCycleDateRange = (
  targetDate,
  rosterStartDate,
  repetition,
  weekStartDay
) => {
  const cycleDays = Math.max(1, (Number(repetition) || 1) * 7);
  const selected = startOfDay(targetDate);
  const rawRosterStart = rosterStartDate
    ? startOfDay(new Date(rosterStartDate))
    : selected;
  // Gốc chu kỳ = đầu tuần chứa ngày start roster → start luôn thuộc Week 1
  const rosterStart = alignToContainingWeekStartDay(rawRosterStart, weekStartDay);
  const diffDays = differenceInCalendarDays(selected, rosterStart);
  const offsetInCycle = ((diffDays % cycleDays) + cycleDays) % cycleDays;
  const cycleStart = addDays(selected, -offsetInCycle);

  return {
    startDate: cycleStart,
    endDate: addDays(cycleStart, cycleDays - 1),
    weekIndex: Math.floor(offsetInCycle / 7),
  };
};

const ShiftAndRosterView = ({
  selectedRows,
  shiftsData,
  usersData,
  rosters,
  handleActiveWeekTabIndex,
  activeWeekTabIndex,
  onRefresh,
  selectedShiftDateTime,
  setSelectedShiftDateTime,
  updateRoster,
  getShiftSelected,
  allUser,
  GroupData,
  refreshData,
  shiftColorMap,
  usersDataByGroup,
  handleRefreshData,
  loadingRoster
}) => {
  const { t } = useTranslation();
  const { genaralSettingDateFormat, isAdmin, isEmployee, weekStartDay } = useAuth();
  const [show, hide] = useLoading();

  const weeks = useMemo(
    () => Array.from({ length: selectedRows.repetition || 0 }),
    [selectedRows]
  );

  const dateFormat = genaralSettingDateFormat || "dd/MM/yyyy";

  const weekTabRanges = useMemo(() => {
    if (!selectedShiftDateTime?.startDate) return [];

    const cycleStart = startOfDay(selectedShiftDateTime.startDate);

    return weeks.map((_, index) => {
      const weekStart = addDays(cycleStart, index * 7);
      const weekEnd = addDays(weekStart, 6);

      return {
        startDate: weekStart,
        endDate: weekEnd,
        label: `${format(weekStart, dateFormat)} - ${format(weekEnd, dateFormat)}`,
      };
    });
  }, [dateFormat, selectedShiftDateTime?.startDate, weeks]);

  const [checked] = useState(true);
  const [openAddRosterItems, setOpenAddRosterItems] = useState(false);
  const [openAddUserRoster, setOpenAddUserRoster] = useState(false);

  useEffect(() => {
    show();
    hide();
  }, [refreshData]);

  // Tour: mở dialog Add User / Add Roster Item từ detail
  useEffect(() => {
    window.__openTourAddUserRoster = () => {
      flushSync(() => {
        setOpenAddRosterItems(false);
        setOpenAddUserRoster(true);
      });
    };
    window.__closeTourAddUserRoster = () => {
      flushSync(() => setOpenAddUserRoster(false));
    };
    window.__openTourAddRosterItems = () => {
      flushSync(() => {
        setOpenAddUserRoster(false);
        setOpenAddRosterItems(true);
      });
    };
    window.__closeTourAddRosterItems = () => {
      flushSync(() => setOpenAddRosterItems(false));
    };
    return () => {
      delete window.__openTourAddUserRoster;
      delete window.__closeTourAddUserRoster;
      delete window.__openTourAddRosterItems;
      delete window.__closeTourAddRosterItems;
    };
  }, []);

  const totalDays = selectedRows.repetition * 7;
  const rangeDays = totalDays - 1;

  const isValidWeekStartDate = useCallback(
    (date) => {
      if (!date || !weekStartDay) return true;

      const weekStartDayIndex = WEEK_DAY_NAME_TO_INDEX[weekStartDay];
      if (weekStartDayIndex === undefined) return true;

      return startOfDay(date).getDay() === weekStartDayIndex;
    },
    [weekStartDay]
  );

  const showInvalidWeekStartDayMessage = useCallback(() => {
    const translationKey = WEEK_DAY_NAME_TO_TRANSLATION_KEY[weekStartDay];
    const dayLabel = translationKey ? t(translationKey) : weekStartDay;

    toast.warn(t("common.date-must-be-week-start-day", { day: dayLabel }));
  }, [t, weekStartDay]);

  const applyShiftDateRange = useCallback(
    (targetDate) => {
      const { startDate, endDate, weekIndex } = getRosterCycleDateRange(
        targetDate,
        selectedRows?.startDate,
        selectedRows?.repetition,
        weekStartDay
      );

      setSelectedShiftDateTime({ startDate, endDate });
      handleActiveWeekTabIndex(weekIndex);
    },
    [
      handleActiveWeekTabIndex,
      selectedRows?.repetition,
      selectedRows?.startDate,
      setSelectedShiftDateTime,
      weekStartDay,
    ]
  );

  const handleRangeStartDateChange = useCallback(
    ([start]) => {
      if (!start) return;

      const newStart = startOfDay(start);
      if (!isValidWeekStartDate(newStart)) {
        showInvalidWeekStartDayMessage();
        return;
      }

      applyShiftDateRange(newStart);
    },
    [applyShiftDateRange, isValidWeekStartDate, showInvalidWeekStartDayMessage]
  );

  const handleSubtractDays = () => {
    setSelectedShiftDateTime((prev) => ({
      startDate: addDays(prev.startDate, -7 * selectedRows.repetition),
      endDate: addDays(prev.endDate, -7 * selectedRows.repetition),
    }));
  };

  const handleAddDays = () => {
    setSelectedShiftDateTime((prev) => ({
      startDate: addDays(prev.startDate, 7 * selectedRows.repetition),
      endDate: addDays(prev.endDate, 7 * selectedRows.repetition),
    }));
  };

  const handleStartDateChange = (date) => {
    if (!date) return;

    const start = startOfDay(date);
    if (!isValidWeekStartDate(start)) {
      showInvalidWeekStartDayMessage();
      return;
    }

    applyShiftDateRange(start);
  };

  const handleEndDateChange = (date) => {
    if (!date) return;
    const end = startOfDay(date);
    const start = addDays(end, -rangeDays);
    setSelectedShiftDateTime({ startDate: start, endDate: end });
  };

  const shiftOptions = useMemo(
    () =>
      (shiftsData || []).map((shift) => ({
        ...shift,
        shiftName:
          shift.shiftName === "DEFAULT" ? t("common.default") : shift.shiftName,
      })),
    [shiftsData, t]
  );

  const handleShiftChange = (_name, shift) => {
    getShiftSelected({
      selectedShift: shift?.shiftId ?? "",
    });
  };

  const handleGoToToday = () => {
    applyShiftDateRange(startOfDay(new Date()));
  };

  return (
    <div className="shift-and-roster-view">
      {!!selectedRows.rosterName ? (
        <>
          {/* ===== TOOLBAR ===== */}
          <div
            className="flex flex-col xl2:flex-row xl2:items-center gap-2 my-2"
            data-tour="shift-and-roster-filter-input"
          >
            {/* Date navigation */}
            <div className="flex items-center gap-1 w-full xl2:w-auto">
              {/* LEFT */}
              <div
                onClick={handleSubtractDays}
                className="flex items-center justify-center w-8 h-10 cursor-pointer select-none"
              >
                <ArrowBackIosIcon fontSize="small" className="text-[#1F2937]" />
              </div>

              {/* RANGE PICKER */}
              <CustomDateTimePicker
                mode="range"
                value={[
                  selectedShiftDateTime.startDate,
                  selectedShiftDateTime.endDate,
                ]}
                maxDate={addDays(new Date(), 365)}
                onChange={handleRangeStartDateChange}
                disableTime
                showOnlyDate
              />

              {/* RIGHT */}
              <div
                onClick={handleAddDays}
                className="flex items-center justify-center w-8 h-10 cursor-pointer select-none"
              >
                <ArrowForwardIosIcon fontSize="small" className="text-[#1F2937]" />
              </div>

            </div>

            <Tooltip title={t("button.select-today")} arrow>
            <div
              className="flex items-center justify-center w-10 h-10 cursor-pointer select-none border border-[#E5E7EB] rounded-[6px] mr-2"
              onClick={handleGoToToday}
            >
              <CalendarToday fontSize="small" className="text-[#1F2937]" />
            </div>
            </Tooltip>
            {/* Shift filter */}
            <div className="w-full xl2:w-[300px] mr-2">
              <CustomAutocomplete
                options={shiftOptions}
                freeSolo
                placeholder={t("common.content-162")}
                onDataChange={handleShiftChange}
                name="shiftId"
                dataType="shift"
              />
            </div>

            {/* Actions */}
            {isAdmin && (
              <div className="flex flex-col xl2:flex-row gap-4">
                <div data-tour="shift-and-roster-add-user-button">
                  <ControlpanelPrimaryButtonV2
                    onClick={() => setOpenAddUserRoster(true)}
                  >
                    <AddUserGroupIconpxSvg />
                    {t("button.add-more-users")}
                  </ControlpanelPrimaryButtonV2>
                </div>

                {checked && (
                  <div data-tour="shift-and-roster-add-rosteritem">
                    <ControlpanelPrimaryButton
                      onClick={() => setOpenAddRosterItems(true)}
                    >
                      <AddIcon className="!text-[16px]" />
                      {t("button.add-roster-item")}
                    </ControlpanelPrimaryButton>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ===== WEEK TABS ===== */}
          <div className="mt-5 rounded-md overflow-hidden border border-[#E5E7EB] rounded-[10px]">
            <div className="flex xl2:grid xl2:grid-cols-8 2xl:grid-cols-9
                            gap-1 p-1 bg-white overflow-x-auto xl2:overflow-visible items-center">
              {weeks.map((_, index) => (
                <div
                  key={index}
                  onClick={() => handleActiveWeekTabIndex(index)}
                  className={`
    weeks-tab_items
    flex flex-col items-center justify-center
    min-w-[120px] xl2:min-w-0
    ${activeWeekTabIndex === index ? "active" : ""}
  `}
                >
                  <p className="text-center">
                    {t("common.week")} {index + 1}
                  </p>
                  {/* {weekTabRanges[index]?.label && (
                    <p className="text-center text-xs font-normal text-[#6B7280]">
                      {weekTabRanges[index].label}
                    </p>
                  )} */}
                </div>
              ))}
            </div>

            {/* ===== VIEW ===== */}
            <LoadingProvider>
              <div className="overflow-x-auto xl:overflow-visible flex-1 min-w-0">
                {checked ? (
                 <ClassicView
                    activeWeek={activeWeekTabIndex}
                    selectedRows={selectedRows}
                    usersData={usersData}
                    rosters={rosters}
                    shiftsData={shiftsData}
                    onRefresh={onRefresh}
                    updateRoster={updateRoster}
                    genaralSettingDateFormat={genaralSettingDateFormat}
                    refreshData={refreshData}
                    shiftColorMap={shiftColorMap}
                    usersDataByGroup={usersDataByGroup}
                    GroupData={GroupData}
                    allUser={allUser}
                    handleRefreshData={handleRefreshData}
                    rosterId={selectedRows?.rosterId}
                    startDate={selectedShiftDateTime.startDate}
                    loadingRoster={loadingRoster}
                  />
                ) : (
                  <SimpleView />
                )}
              </div>
            </LoadingProvider>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="font-semibold text-xl text-blue-500">
            {t("common.create-a-roster-to-schedule-work")}!
          </p>
          <p>{t("common.roster-simplify-work-scheduling")}.</p>
        </div>
      )}

      {/* ===== DIALOGS ===== */}
      {openAddRosterItems && (
        <AddRosterItemsDialog
          onClose={() => setOpenAddRosterItems(false)}
          usersData={usersData}
          shiftsData={shiftsData}
          selectedRows={selectedRows}
          onRefresh={onRefresh}
          activeWeek={activeWeekTabIndex}
          genaralSettingDateFormat={genaralSettingDateFormat}
        />
      )}

      {openAddUserRoster && (
        <AddUserRoster
          onClose={() => setOpenAddUserRoster(false)}
          allUser={allUser}
          onRefresh={onRefresh}
          selectedRows={selectedRows}
          usersData={usersData}
          GroupData={GroupData}
        />
      )}
    </div>
  );
};

export default ShiftAndRosterView;